import React, { useState, useEffect } from "react";
import { getProfile } from "../api/getProfile";
import { updateProfile } from "../api/updateProfile";
import UserProfile from "../types/UserProfile";
import {
  Button,
  Card,
  Box,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
  Alert,
  Grid,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { CustomThemeProvider } from "../theme";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";

function Profile() {
  const [isLoading, setIsLoading] = useState(true);
  const [userInfo, setUserInfo] = useState<UserProfile | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [editedData, setEditedData] = useState<UserProfile | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    async function fetchProfile() {
      try {
        const data = await getProfile();
        setUserInfo(data);
        setEditedData(data);
        setIsLoading(false);
      } catch (err) {
        setError("Failed to load profile data");
        setIsLoading(false);
      }
    }
    fetchProfile();
  }, []);

  const handleEdit = () => {
    setEditMode(true);
    setError(null);
    setSuccess(null);
  };

  const handleCancel = () => {
    setEditMode(false);
    setEditedData(userInfo); // Reset to original data
    setError(null);
    setSuccess(null);
  };

  const handleSave = async () => {
    if (!editedData) return;

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const updatedProfile = await updateProfile(editedData);
      setUserInfo(updatedProfile);
      setEditedData(updatedProfile);
      setEditMode(false);
      setSuccess("Profile updated successfully!");
    } catch (err: any) {
      console.error("Profile update error:", err);
      if (err && typeof err === "object" && err.username) {
        setError(
          "Username is already taken. Please choose a different username.",
        );
      } else if (err && typeof err === "string" && err.includes("username")) {
        setError(
          "Username is already taken. Please choose a different username.",
        );
      } else {
        setError("Failed to update profile. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = (field: keyof UserProfile, value: any) => {
    if (editedData) {
      setEditedData({
        ...editedData,
        [field]: value,
      });
    }
  };

  if (isLoading) return <div>Loading...</div>;

  return (
    <CustomThemeProvider>
      <Box
        sx={{
          width: "100%",
          marginBottom: "1em",
          backgroundColor: "#121212",
          borderRadius: "10px",
          padding: "1em",
        }}
      >
        <Card variant="outlined" sx={{ padding: 2 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="start"
            mb={2}
          >
            <Typography gutterBottom variant="h5" component="div">
              {editMode
                ? "Edit Profile"
                : `${userInfo?.firstName} ${userInfo?.lastName}`}
            </Typography>
            {!editMode ? (
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={handleEdit}
                size="small"
              >
                Edit
              </Button>
            ) : (
              <Box>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleCancel}
                  size="small"
                  sx={{ mr: 1 }}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  size="small"
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save"}
                </Button>
              </Box>
            )}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {editMode ? (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={editedData?.firstName || ""}
                  onChange={(e) =>
                    handleFieldChange("firstName", e.target.value)
                  }
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={editedData?.lastName || ""}
                  onChange={(e) =>
                    handleFieldChange("lastName", e.target.value)
                  }
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Username"
                  value={editedData?.username || ""}
                  onChange={(e) =>
                    handleFieldChange("username", e.target.value)
                  }
                  size="small"
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  value={editedData?.email || ""}
                  size="small"
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={editedData?.phoneNumber || ""}
                  onChange={(e) =>
                    handleFieldChange("phoneNumber", e.target.value)
                  }
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date of Birth"
                  type="date"
                  value={editedData?.dateOfBirth || ""}
                  onChange={(e) =>
                    handleFieldChange("dateOfBirth", e.target.value)
                  }
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editedData?.reminderEmail || false}
                      onChange={(e) =>
                        handleFieldChange("reminderEmail", e.target.checked)
                      }
                    />
                  }
                  label="Email Reminders"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editedData?.reminderBrowser || false}
                      onChange={(e) =>
                        handleFieldChange("reminderBrowser", e.target.checked)
                      }
                    />
                  }
                  label="Browser Reminders"
                />
              </Grid>
            </Grid>
          ) : (
            <>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ marginBottom: 2 }}
              >
                Email: {userInfo?.email}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ marginBottom: 2 }}
              >
                Username: {userInfo?.username || "Not set"}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ marginBottom: 2 }}
              >
                Phone Number: {userInfo?.phoneNumber || "Not set"}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ marginBottom: 2 }}
              >
                Date of Birth: {userInfo?.dateOfBirth || "Not set"}
              </Typography>

              {userInfo?.feedback && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ marginBottom: 2 }}
                >
                  Feedback: {userInfo.feedback}
                </Typography>
              )}
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ marginBottom: 2 }}
              >
                Email Reminders:{" "}
                {userInfo?.reminderEmail ? "Enabled" : "Disabled"}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ marginBottom: 2 }}
              >
                Browser Reminders:{" "}
                {userInfo?.reminderBrowser ? "Enabled" : "Disabled"}
              </Typography>
            </>
          )}
        </Card>
      </Box>
    </CustomThemeProvider>
  );
}

export default Profile;
